import { useCallback, useState } from 'react';

export const copyToClipboardStates = {
  idle: 'idle',
  loading: 'loading',
  success: 'success',
  failure: 'failure',
} as const;

type CopyToClipboardState = typeof copyToClipboardStates[keyof typeof copyToClipboardStates];

interface IUseCopyToClipboardOptions<TValue = string> {
  onSuccess?: (textToCopy: TValue) => void | Promise<void>,
  onError?: (error: unknown, textToCopy: TValue) => void | Promise<void>,
}

/**
 * Copies text to the clipboard and tracks the result: `[copy, state, reset]`.
 */
export const useCopyToClipboard = <TValue extends string = string>(
  { onSuccess, onError }: IUseCopyToClipboardOptions<TValue> = {},
): readonly [(textToCopy: TValue) => Promise<void>, CopyToClipboardState, () => void] => {
  const [state, setState] = useState<CopyToClipboardState>(copyToClipboardStates.idle);

  const copy = useCallback(async (textToCopy: TValue): Promise<void> => {
    setState(copyToClipboardStates.loading);
    try {
      await navigator.clipboard.writeText(textToCopy);
      await onSuccess?.(textToCopy);
      setState(copyToClipboardStates.success);
    } catch (error) {
      await onError?.(error, textToCopy);
      setState(copyToClipboardStates.failure);
    }
  }, [onSuccess, onError]);

  const reset = useCallback(() => setState(copyToClipboardStates.idle), []);

  return [copy, state, reset] as const;
};

export const useCopyToClipboardState = (state: CopyToClipboardState) => ({
  isIdle: state === copyToClipboardStates.idle,
  isLoading: state === copyToClipboardStates.loading,
  isSuccess: state === copyToClipboardStates.success,
  isFailure: state === copyToClipboardStates.failure,
});
